import { motion, useScroll, useTransform } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { HERO_IMAGES } from "@/site.config";

const line = {
  hidden: { y: "115%" },
  show: (i = 0) => ({
    y: "0%",
    transition: {
      delay: 0.25 + i * 0.14,
      duration: 1.15,
      ease: [0.65, 0, 0.05, 1],
    },
  }),
};

export default function Hero({ onOpenForm }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const yImg = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const yContent = useTransform(scrollYProgress, [0, 1], ["0%", "-12%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.85], [1, 0.25]);

  // Layered cinematic loop through hero images.
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => {
      setIdx((i) => (i + 1) % HERO_IMAGES.length);
    }, 5500);
    return () => clearInterval(t);
  }, []);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100vh] w-full overflow-hidden isolate"
    >
      {/* Animated cinematic backdrop */}
      <motion.div style={{ y: yImg }} className="absolute inset-0" aria-hidden>
        <div className="relative w-full h-full overflow-hidden bg-[#050505]">
          {HERO_IMAGES.map((src, i) => (
            <div
              key={src}
              className={`absolute inset-0 transition-opacity duration-[2200ms] ease-out ${
                i === idx ? "opacity-100" : "opacity-0"
              }`}
            >
              {/* Ken-Burns wrapper */}
              <div
                className="absolute inset-0 will-change-transform"
                style={{
                  animation: "kenburns 18s ease-in-out infinite alternate",
                  animationDelay: `${i * -6}s`,
                }}
              >
                <img
                  src={src}
                  alt=""
                  className="w-full h-full object-cover"
                  style={{
                    filter: "saturate(0.85) contrast(1.05) brightness(0.55)",
                  }}
                  loading={i === 0 ? "eager" : "lazy"}
                />
              </div>
            </div>
          ))}
        </div>

        {/* Gradient / vignette overlay */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse at 20% 20%, rgba(212,175,55,0.18) 0%, transparent 45%), linear-gradient(to bottom, rgba(3,3,3,0.55) 0%, rgba(3,3,3,0.72) 55%, rgba(3,3,3,0.95) 100%)",
          }}
        />

        {/* Gold shimmer particles (pure CSS) */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {[...Array(14)].map((_, i) => (
            <span
              key={i}
              className="absolute block rounded-full bg-[#D4AF37]/70 shadow-[0_0_8px_2px_rgba(212,175,55,0.45)]"
              style={{
                width: `${2 + (i % 3)}px`,
                height: `${2 + (i % 3)}px`,
                left: `${(i * 73) % 100}%`,
                top: `${(i * 53) % 100}%`,
                animation: `shimmer ${8 + (i % 5) * 2}s ease-in-out ${(i % 7) * -1.2}s infinite`,
                opacity: 0.35,
              }}
            />
          ))}
        </div>
      </motion.div>

      {/* Global keyframes for hero motion */}
      <style>{`
        @keyframes kenburns {
          0% { transform: scale(1.05) translate3d(0,0,0); }
          100% { transform: scale(1.18) translate3d(-2%, -1.5%, 0); }
        }
        @keyframes shimmer {
          0%, 100% { transform: translateY(0px); opacity: 0.15; }
          50% { transform: translateY(-14px); opacity: 0.75; }
        }
      `}</style>

      {/* Top meta line */}
      <div className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-10 pt-24 sm:pt-28 md:pt-32">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.9 }}
          className="flex items-center gap-3 text-[10px] sm:text-[11px] md:text-[12px] uppercase tracking-[0.24em] sm:tracking-[0.28em] text-white/60"
        >
          <span className="h-px w-8 md:w-12 bg-[#D4AF37]" />
          <span className="text-[#D4AF37]">Ch. 01</span>
          <span>India's Leading Nightlife Consultant</span>
        </motion.div>
      </div>

      {/* Headline */}
      <motion.div
        style={{ y: yContent, opacity }}
        className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-10 mt-8 md:mt-14"
      >
        <h2
          data-testid="hero-headline"
          className="font-display text-white leading-[0.92] tracking-[-0.02em] text-[38px] xs:text-[46px] sm:text-[64px] md:text-[92px] lg:text-[128px] break-words"
        >
          {[
            ["Increase Your", ""],
            ["Nightclub's", " Profits"],
            ["Without", " Spending"],
            ["More on", " Marketing."],
          ].map(([a, b], i) => (
            <span key={i} className="line-mask">
              <motion.span
                variants={line}
                initial="hidden"
                animate="show"
                custom={i}
                className="inline-block will-change-transform"
              >
                {a}
                {b && (
                  <em className="font-editorial italic text-[#D4AF37] font-light">
                    {b}
                  </em>
                )}
              </motion.span>
            </span>
          ))}
        </h2>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.25, duration: 0.9 }}
          className="mt-8 md:mt-10 max-w-2xl text-[14px] sm:text-[15px] md:text-[17px] text-white/70 leading-relaxed"
        >
          Helping nightclubs, bars & hospitality businesses increase revenue,
          improve operations and create better guest experiences through
          strategic consulting and on-ground audits.
        </motion.p>

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.45, duration: 0.9 }}
          className="mt-8 md:mt-10 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4"
        >
          <button
            onClick={() => onOpenForm?.("paid-call")}
            data-testid="hero-cta-paid-call"
            className="group inline-flex items-center justify-between sm:justify-start gap-3 rounded-full bg-[#D4AF37] text-black px-5 sm:px-6 py-3.5 sm:py-4 text-[13px] sm:text-[14px] font-medium hover:bg-[#f3d568] transition-colors w-full sm:w-auto"
          >
            Book a Paid Business Call
            <span className="w-6 h-6 rounded-full bg-black/10 flex items-center justify-center group-hover:translate-x-1 transition-transform">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                <path
                  d="M5 12h14M13 5l7 7-7 7"
                  stroke="currentColor"
                  strokeWidth="2"
                />
              </svg>
            </span>
          </button>

          <button
            onClick={() => onOpenForm?.("quick-chat")}
            data-testid="hero-cta-quick-chat"
            className="group inline-flex items-center justify-center gap-3 rounded-full border border-white/25 text-white px-5 sm:px-6 py-3.5 sm:py-4 text-[13px] sm:text-[14px] font-medium hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors w-full sm:w-auto"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] animate-pulse" />
            FREE 3-minute Quick Chat
          </button>

          <button
            onClick={() => onOpenForm?.("audit")}
            data-testid="hero-cta-audit"
            className="hidden md:inline-flex items-center gap-3 rounded-full border border-white/15 text-white/80 px-6 py-4 text-[14px] font-medium hover:text-white hover:border-white/40 transition-colors"
          >
            Schedule a Nightclub Audit
          </button>
        </motion.div>

        {/* Trust badges */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.75, duration: 1 }}
          className="mt-14 md:mt-20 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10 max-w-3xl"
        >
          {[
            ["20+", "Years in Nightlife"],
            ["1000+", "Events Worldwide"],
            ["50+", "Venues Audited"],
            ["Pan-India", "On-ground reach"],
          ].map(([k, v]) => (
            <div key={v} className="flex flex-col">
              <div className="font-display text-[28px] md:text-[36px] leading-none text-white">
                {k}
              </div>
              <div className="mt-2 text-[11px] uppercase tracking-[0.2em] text-white/45">
                {v}
              </div>
            </div>
          ))}
        </motion.div>
      </motion.div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="hidden md:flex absolute bottom-8 left-6 md:left-10 z-10 items-center gap-3 text-white/50 text-[11px] tracking-[0.28em] uppercase"
      >
        <span className="h-8 w-px bg-white/25" />
        Scroll
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="hidden md:flex absolute bottom-8 right-6 md:right-10 z-10 items-center gap-3 text-white/50 text-[11px] tracking-[0.28em] uppercase"
      >
        Est. Rubiemaze &middot; Kolkata &middot; India
      </motion.div>
    </section>
  );
}
